export type AdvancedReadinessReasonCode =
  | 'no_qualified_history'
  | 'insufficient_randomized_volume'
  | 'insufficient_overlap'
  | 'outcomes_not_mature'
  | 'excessive_missingness'
  | 'metric_contract_not_stable'
  | 'insufficient_holdout_replication'
  | 'calibration_below_threshold'
  | 'privacy_coverage_below_threshold'
  | 'critical_drift'
  | 'operational_safety_failed'
  | 'kill_switch_not_ready';

export interface AdvancedReadinessSafeRecord {
  projectId: string;
  experimentId: string;
  state: 'blocked' | 'eligible';
  evaluatedAt: string;
  updatedAt: string;
  fallback: {
    id: string;
    version: string;
  };
  unmetReasons: AdvancedReadinessReasonCode[];
  approvalRequired: true;
}

export interface AdvancedReadinessScope {
  projectId: string;
  experimentId: string;
}

export class AdvancedReadinessPayloadError extends Error {
  constructor(message = 'Advanced readiness payload is invalid') {
    super(message);
    this.name = 'AdvancedReadinessPayloadError';
  }
}

const ROOT_KEYS = new Set([
  'schemaVersion', 'projectId', 'experimentId', 'state', 'revision', 'contract',
  'latestEvaluation', 'latestEvaluationId', 'fallback', 'createdAt', 'updatedAt',
]);
const CONTRACT_KEYS = new Set([
  'schemaVersion', 'contractId', 'contractVersion', 'projectId', 'expectedVersions',
  'thresholds', 'fallback',
]);
const EXPECTED_VERSION_KEYS = new Set([
  'metricContract', 'certification', 'experimentDecision', 'effectLedger',
]);
const THRESHOLD_KEYS = new Set([
  'minimumQualifiedRandomizedUnits', 'minimumOverlap', 'minimumFollowUpDays',
  'maximumMissingness', 'minimumStableMetricDays', 'minimumHoldoutReplications',
  'minimumCalibrationScore', 'minimumPrivacyCoverage', 'maximumDrift',
  'maximumP95LatencyMs',
]);
const FALLBACK_KEYS = new Set(['id', 'version', 'behavior']);
const EVALUATION_KEYS = new Set([
  'schemaVersion', 'contractRef', 'projectId', 'state', 'evaluatedAt', 'dimensions',
  'unmetReasons', 'fallback', 'approvalRequired',
]);
const CONTRACT_REF_KEYS = new Set(['id', 'version']);
const DIMENSION_KEYS = new Set(['id', 'passed', 'reasonCode']);

const DIMENSION_REASONS: Record<string, readonly AdvancedReadinessReasonCode[]> = {
  qualified_randomized_volume: ['no_qualified_history', 'insufficient_randomized_volume'],
  treatment_control_overlap: ['insufficient_overlap'],
  outcome_follow_up: ['outcomes_not_mature'],
  outcome_missingness: ['excessive_missingness'],
  metric_contract_stability: ['metric_contract_not_stable'],
  holdout_replication: ['insufficient_holdout_replication'],
  estimator_calibration: ['calibration_below_threshold'],
  privacy_coverage: ['privacy_coverage_below_threshold'],
  drift: ['critical_drift'],
  operational_safety: ['operational_safety_failed'],
  kill_switch_readiness: ['kill_switch_not_ready'],
};

const REASON_LABELS: Record<AdvancedReadinessReasonCode, string> = {
  no_qualified_history: 'Qualified randomized history is not available',
  insufficient_randomized_volume: 'Qualified randomized volume is below the required minimum',
  insufficient_overlap: 'Treatment and control overlap is below the required minimum',
  outcomes_not_mature: 'Outcome follow-up window is incomplete',
  excessive_missingness: 'Outcome missingness is above the allowed maximum',
  metric_contract_not_stable: 'The metric contract has not been stable long enough',
  insufficient_holdout_replication: 'Holdout replication evidence is missing',
  calibration_below_threshold: 'Estimator calibration is below the required threshold',
  privacy_coverage_below_threshold: 'Privacy-safe evidence coverage is below the required threshold',
  critical_drift: 'Critical evidence drift was detected',
  operational_safety_failed: 'Operational safety or latency requirements did not pass',
  kill_switch_not_ready: 'The rollback kill switch is not ready',
};

export function describeAdvancedReadinessReason(code: AdvancedReadinessReasonCode): string {
  return REASON_LABELS[code];
}

export function parseAdvancedReadinessRecord(
  value: unknown,
  expectedScope: AdvancedReadinessScope,
): AdvancedReadinessSafeRecord | null {
  if (value === null) return null;
  const record = strictObject(value, ROOT_KEYS, 'record');
  requireLiteral(record.schemaVersion, 1, 'record.schemaVersion');
  const projectId = boundedString(record.projectId, 'record.projectId');
  const experimentId = boundedString(record.experimentId, 'record.experimentId');
  if (projectId !== expectedScope.projectId || experimentId !== expectedScope.experimentId) {
    throw new AdvancedReadinessPayloadError('Advanced readiness payload scope does not match the requested experiment');
  }
  const state = readinessState(record.state, 'record.state');
  safeInteger(record.revision, 'record.revision');
  isoString(record.createdAt, 'record.createdAt');
  const updatedAt = isoString(record.updatedAt, 'record.updatedAt');

  const contract = strictObject(record.contract, CONTRACT_KEYS, 'record.contract');
  requireLiteral(contract.schemaVersion, 1, 'record.contract.schemaVersion');
  const contractId = publicIdentifier(contract.contractId, 'record.contract.contractId');
  const contractVersion = publicIdentifier(contract.contractVersion, 'record.contract.contractVersion');
  if (boundedString(contract.projectId, 'record.contract.projectId') !== projectId) {
    throw new AdvancedReadinessPayloadError('Advanced readiness contract belongs to another project');
  }
  stringMap(contract.expectedVersions, EXPECTED_VERSION_KEYS, 'record.contract.expectedVersions');
  numberMap(contract.thresholds, THRESHOLD_KEYS, 'record.contract.thresholds');
  const contractFallback = parseFallback(contract.fallback, 'record.contract.fallback');
  const fallback = parseFallback(record.fallback, 'record.fallback');
  if (fallback.id !== contractFallback.id || fallback.version !== contractFallback.version) {
    throw new AdvancedReadinessPayloadError('Advanced readiness fallback identity is inconsistent');
  }

  const evaluation = strictObject(record.latestEvaluation, EVALUATION_KEYS, 'record.latestEvaluation');
  requireLiteral(evaluation.schemaVersion, 1, 'record.latestEvaluation.schemaVersion');
  const contractRef = strictObject(evaluation.contractRef, CONTRACT_REF_KEYS, 'record.latestEvaluation.contractRef');
  const contractRefId = publicIdentifier(contractRef.id, 'record.latestEvaluation.contractRef.id');
  const contractRefVersion = publicIdentifier(contractRef.version, 'record.latestEvaluation.contractRef.version');
  if (contractRefId !== contractId || contractRefVersion !== contractVersion) {
    throw new AdvancedReadinessPayloadError('Advanced readiness evaluation contract reference is inconsistent');
  }
  if (boundedString(evaluation.projectId, 'record.latestEvaluation.projectId') !== projectId) {
    throw new AdvancedReadinessPayloadError('Advanced readiness evaluation belongs to another project');
  }
  if (readinessState(evaluation.state, 'record.latestEvaluation.state') !== state) {
    throw new AdvancedReadinessPayloadError('Advanced readiness record and evaluation states do not match');
  }
  const evaluatedAt = isoString(evaluation.evaluatedAt, 'record.latestEvaluation.evaluatedAt');
  const evaluationFallback = parseFallback(evaluation.fallback, 'record.latestEvaluation.fallback');
  if (evaluationFallback.id !== fallback.id || evaluationFallback.version !== fallback.version) {
    throw new AdvancedReadinessPayloadError('Advanced readiness evaluation fallback is inconsistent');
  }
  requireLiteral(evaluation.approvalRequired, true, 'record.latestEvaluation.approvalRequired');
  boundedString(record.latestEvaluationId, 'record.latestEvaluationId');

  const dimensions = arrayValue(evaluation.dimensions, 'record.latestEvaluation.dimensions', 11);
  if (dimensions.length !== Object.keys(DIMENSION_REASONS).length) {
    throw new AdvancedReadinessPayloadError('Advanced readiness dimension coverage is incomplete');
  }
  const dimensionIds = new Set<string>();
  const dimensionReasons = new Set<AdvancedReadinessReasonCode>();
  for (const [index, dimensionValue] of dimensions.entries()) {
    const dimension = strictObject(
      dimensionValue,
      DIMENSION_KEYS,
      `record.latestEvaluation.dimensions[${index}]`,
    );
    const id = boundedString(dimension.id, `record.latestEvaluation.dimensions[${index}].id`);
    if (!(id in DIMENSION_REASONS) || dimensionIds.has(id)) {
      throw new AdvancedReadinessPayloadError('Advanced readiness dimensions are invalid or duplicated');
    }
    dimensionIds.add(id);
    if (typeof dimension.passed !== 'boolean') {
      throw new AdvancedReadinessPayloadError('Advanced readiness dimension passed flag is invalid');
    }
    if (dimension.passed === true && dimension.reasonCode !== undefined) {
      throw new AdvancedReadinessPayloadError('A passed readiness dimension cannot carry a blocker');
    }
    if (dimension.passed === false && dimension.reasonCode === undefined) {
      throw new AdvancedReadinessPayloadError('A failed readiness dimension must carry a blocker');
    }
    if (dimension.reasonCode !== undefined) {
      const reason = readinessReason(
        dimension.reasonCode,
        `record.latestEvaluation.dimensions[${index}].reasonCode`,
      );
      if (!DIMENSION_REASONS[id].includes(reason)) {
        throw new AdvancedReadinessPayloadError('Advanced readiness dimension blocker is inconsistent');
      }
      dimensionReasons.add(reason);
    }
  }

  const unmetReasons = arrayValue(evaluation.unmetReasons, 'record.latestEvaluation.unmetReasons', 11)
    .map((reason, index) => readinessReason(reason, `record.latestEvaluation.unmetReasons[${index}]`));
  if (new Set(unmetReasons).size !== unmetReasons.length) {
    throw new AdvancedReadinessPayloadError('Advanced readiness unmet reasons are duplicated');
  }
  if (unmetReasons.some((reason) => !dimensionReasons.has(reason))
    || [...dimensionReasons].some((reason) => !unmetReasons.includes(reason))) {
    throw new AdvancedReadinessPayloadError('Advanced readiness unmet gates do not match its dimensions');
  }
  if ((state === 'eligible' && unmetReasons.length > 0) || (state === 'blocked' && unmetReasons.length === 0)) {
    throw new AdvancedReadinessPayloadError('Advanced readiness state does not match its unmet gates');
  }

  return {
    projectId,
    experimentId,
    state,
    evaluatedAt,
    updatedAt,
    fallback: { id: fallback.id, version: fallback.version },
    unmetReasons,
    approvalRequired: true,
  };
}

function strictObject(value: unknown, keys: Set<string>, path: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new AdvancedReadinessPayloadError(`${path} must be an object`);
  }
  const record = value as Record<string, unknown>;
  if (Object.keys(record).some((key) => !keys.has(key))) {
    throw new AdvancedReadinessPayloadError(`${path} contains an unsupported field`);
  }
  return record;
}

function parseFallback(value: unknown, path: string): { id: string; version: string } {
  const fallback = strictObject(value, FALLBACK_KEYS, path);
  requireLiteral(fallback.behavior, 'current_static_allocation', `${path}.behavior`);
  return {
    id: publicIdentifier(fallback.id, `${path}.id`),
    version: publicIdentifier(fallback.version, `${path}.version`),
  };
}

function stringMap(value: unknown, keys: Set<string>, path: string): void {
  const record = strictObject(value, keys, path);
  for (const key of keys) boundedString(record[key], `${path}.${key}`);
}

function numberMap(value: unknown, keys: Set<string>, path: string): void {
  const record = strictObject(value, keys, path);
  for (const key of keys) {
    if (typeof record[key] !== 'number' || !Number.isFinite(record[key])) {
      throw new AdvancedReadinessPayloadError(`${path}.${key} must be a finite number`);
    }
  }
}

function arrayValue(value: unknown, path: string, maximum: number): unknown[] {
  if (!Array.isArray(value) || value.length > maximum) {
    throw new AdvancedReadinessPayloadError(`${path} must be a bounded array`);
  }
  return value;
}

function boundedString(value: unknown, path: string): string {
  if (typeof value !== 'string' || !value.trim() || value.length > 200) {
    throw new AdvancedReadinessPayloadError(`${path} must be a bounded non-empty string`);
  }
  return value.trim();
}

function publicIdentifier(value: unknown, path: string): string {
  const identifier = boundedString(value, path);
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(identifier)) {
    throw new AdvancedReadinessPayloadError(`${path} must be a safe public identifier`);
  }
  return identifier;
}

function isoString(value: unknown, path: string): string {
  const text = boundedString(value, path);
  if (!Number.isFinite(Date.parse(text))) throw new AdvancedReadinessPayloadError(`${path} must be ISO-8601`);
  return text;
}

function safeInteger(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new AdvancedReadinessPayloadError(`${path} must be a non-negative safe integer`);
  }
  return value;
}

function readinessState(value: unknown, path: string): 'blocked' | 'eligible' {
  if (value !== 'blocked' && value !== 'eligible') {
    throw new AdvancedReadinessPayloadError(`${path} is not a supported read-only state`);
  }
  return value;
}

function readinessReason(value: unknown, path: string): AdvancedReadinessReasonCode {
  if (typeof value !== 'string' || !(value in REASON_LABELS)) {
    throw new AdvancedReadinessPayloadError(`${path} is not a supported reason code`);
  }
  return value as AdvancedReadinessReasonCode;
}

function requireLiteral<T extends string | number | boolean>(value: unknown, expected: T, path: string): void {
  if (value !== expected) throw new AdvancedReadinessPayloadError(`${path} must equal ${String(expected)}`);
}
